import React, { useCallback, useState, useContext, createContext, useEffect, useMemo } from 'react'
import api from '../services/api'

import { toast } from 'react-toastify'

type IRoles = 'admin' | 'driver' | 'manager' | 'attendant'

interface IAuthData {
  token: string
  is_token_valid: string
  role: IRoles
  id?: string
}

interface IAuthContextData {
  authData: IAuthData
  signIn(email: string, password: string): Promise<void>
  signOut(): Promise<void>
}

interface IResponseData {
  user_id?: string
  auth: {
    token: string
  }
  roles: IRoles
}

const AuthContext = createContext({} as IAuthContextData)

const AuthProvider: React.FC<any> = ({ children }) => {
  const [authData, setAuthData] = useState(() => {
    const token = localStorage.getItem('@smartdelivery:token')
    const is_token_valid = localStorage.getItem('@smartdelivery:is_token_valid') ?? 'false'
    const role = localStorage.getItem('@smartdelivery:role') as IRoles
    const id = localStorage.getItem('@smartdelivery:id') ?? undefined

    if (role && token && is_token_valid === 'true') {
      api.defaults.headers.common['Authorization'] = `Bearer ${JSON.parse(token)}`

      const authData: IAuthData = {
        token: JSON.parse(token),
        is_token_valid: JSON.parse(is_token_valid),
        role: JSON.parse(role),
      }
      if (id !== undefined) authData.id = JSON.parse(id)

      return authData
    }
    return {} as IAuthData
  })

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const response = await api.post<IResponseData>('/sessions', {
        email,
        password,
      })

      const data = response.data
      const roles = data.roles
      if (roles.includes('driver')) {
        toast.error('Motoristas devem utilizar o aplicativo!')
        throw new Error()
      }

      const { token } = data.auth
      const role = roles[0]
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const userData = {
        is_token_valid: 'true',
        token,
        role: role as IRoles,
        id: data.user_id,
      }

      setAuthData(userData)
      localStorage.setItem('@smartdelivery:token', JSON.stringify(userData.token))
      localStorage.setItem('@smartdelivery:is_token_valid', userData.is_token_valid)
      localStorage.setItem('@smartdelivery:role', JSON.stringify(userData.role))
      localStorage.setItem('@smartdelivery:id', JSON.stringify(userData.id))
      return
    } catch (e: any) {
      if (e.response && e.response.status === 429)
        throw new Error('Excesso de tentativas de login.')
      throw new Error('Falha ao realizar login')
    }
  }, [])

  const signOut = useCallback(async () => {
    try {
      await api.delete('sessions/')
      setAuthData({} as IAuthData)
      localStorage.removeItem('@smartdelivery:token')
      localStorage.removeItem('@smartdelivery:is_token_valid')
      localStorage.removeItem('@smartdelivery:role')
      localStorage.removeItem('@smartdelivery:id')
      api.defaults.headers.common['Authorization'] = ''
    } catch (error) {
      console.error('Error deleting session:', error)
    }    
  }, [])

  useEffect(() => {
    let { token, is_token_valid, role } = authData

    if (!role || !token || is_token_valid === 'false') {
      signOut()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const contextValue = useMemo(() => ({
    authData,
    signIn,
    signOut,
  }), [authData, signIn, signOut])

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

function useAuth(): IAuthContextData {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('You must use useAuth inside an AuthProvider')
  }

  return context
}


export { AuthProvider, useAuth }
